"use client"

import { useState } from "react"
import { HouseholdTier } from "@/schema"
import { pluralize } from "@/utils/text-helpers"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@ui/shad"
import {
  Archive,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  FolderPlus,
  ListPlus,
  Megaphone,
  MoreHorizontal,
  PenLine,
  Plus,
  Type,
  X,
} from "lucide-react"
import { toast } from "sonner"

import { MAX_BULK_EMAIL_RECIPIENTS } from "@/lib/email-limits"
import {
  useBulkAddClientsToProject,
  useBulkAddClientsToSequence,
  useBulkArchiveClientsToPastClient,
  useBulkDemoteClients,
  useBulkRenameClients,
  useBulkUpdateClients,
} from "@/hooks/api/clients/mutations"
import { useBulkMoveHouseholdsToBook } from "@/hooks/api/households/mutations"
import {
  useBulkAddProspectsToProject,
  useBulkAddProspectsToSequence,
  useBulkPromoteProspects,
  useBulkRenameProspects,
  useBulkUpdateProspects,
} from "@/hooks/api/prospects/mutations"
import { useFetchCurrentUser } from "@/hooks/api/users/queries"
import { useBulkEmailCompose } from "@/components/bulk-email"
import { CreateCustomFieldDialog } from "@/components/custom-fields/create-custom-field-dialog"
import { ConfirmDialog } from "@/components/dialogs"
import { BulkRenameDialog } from "@/components/households/bulk-rename-dialog"
import { MoveToBookDialog } from "@/components/households/move-to-book-dialog"
import { BulkAddToProjectDialog } from "@/components/projects/bulk-add-to-project-dialog"
import { BulkAddToSequenceDialog } from "@/components/sequences/bulk-add-to-sequence-dialog"
import { BulkCustomFieldUpdate } from "@/app/[bookId]/clients/bulk-custom-field-update"
import BulkTagSelection from "@/app/[bookId]/clients/bulk-tag-selection"
import BulkTierSelection from "@/app/[bookId]/clients/bulk-tier-selection"

interface CohortBulkActionsProps {
  recordIds: string[]
  recordType: "client" | "prospect"
  recordCount: number
  onClearSelection: () => void
}

export function CohortBulkActions({ recordIds, recordType, recordCount, onClearSelection }: CohortBulkActionsProps) {
  if (recordType === "client") {
    return <ClientCohortActions recordIds={recordIds} recordCount={recordCount} onClearSelection={onClearSelection} />
  }
  return <ProspectCohortActions recordIds={recordIds} recordCount={recordCount} onClearSelection={onClearSelection} />
}

interface CohortActionProps {
  recordIds: string[]
  recordCount: number
  onClearSelection: () => void
}

function ClientCohortActions({ recordIds, recordCount, onClearSelection }: CohortActionProps) {
  const label = pluralize(recordCount, "client")
  const [creatingCustomField, setCreatingCustomField] = useState(false)
  const bulkEmailCompose = useBulkEmailCompose()

  const { mutate: bulkUpdateClients, isPending: isBulkUpdating } = useBulkUpdateClients()
  const { mutate: bulkDemoteClients, isPending: isBulkDemoting } = useBulkDemoteClients()
  const { mutate: bulkArchiveToPastClient, isPending: isBulkArchiving } = useBulkArchiveClientsToPastClient()
  const { mutate: bulkAddClientsToProject, isPending: isBulkStartingProjects } = useBulkAddClientsToProject()
  const { mutate: bulkAddClientsToSequence, isPending: isBulkAddingToSequence } = useBulkAddClientsToSequence()
  const { mutate: bulkRenameClients, isPending: isBulkRenaming } = useBulkRenameClients()
  const { mutate: bulkMoveToBook, isPending: isBulkMoving } = useBulkMoveHouseholdsToBook()
  const [isBulkAddingTag, setIsBulkAddingTag] = useState(false)
  const [showDemoteConfirm, setShowDemoteConfirm] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showAddToProjectDialog, setShowAddToProjectDialog] = useState(false)
  const [showAddToSequenceDialog, setShowAddToSequenceDialog] = useState(false)
  const [showCustomFieldDialog, setShowCustomFieldDialog] = useState(false)
  const [showMoveToBookDialog, setShowMoveToBookDialog] = useState(false)
  const [showRenameDialog, setShowRenameDialog] = useState(false)

  const { data: currentUser } = useFetchCurrentUser()
  const canMoveToBook = (currentUser?.books?.length ?? 0) > 1

  const performingAction =
    isBulkUpdating ||
    isBulkDemoting ||
    isBulkArchiving ||
    isBulkStartingProjects ||
    isBulkAddingToSequence ||
    isBulkAddingTag ||
    isBulkMoving ||
    isBulkRenaming

  function handleBulkUpdateTier(tier: HouseholdTier | null) {
    bulkUpdateClients(
      { client_ids: recordIds, tier },
      {
        onSuccess: () => {
          toast.success(`Updated tier for ${label}`)
        },
      }
    )
  }

  function handleBulkAddTag(tagId: string) {
    setIsBulkAddingTag(true)
    bulkUpdateClients(
      { client_ids: recordIds, tag_id: tagId },
      {
        onSuccess: () => {
          setIsBulkAddingTag(false)
          toast.success(`Added tag to ${label}`)
        },
        onError: () => setIsBulkAddingTag(false),
      }
    )
  }

  function handleBulkUpdateCustomField(fieldKey: string, value: string | string[] | number | boolean | null) {
    bulkUpdateClients(
      { client_ids: recordIds, custom_field_key: fieldKey, custom_field_value: value },
      {
        onSuccess: () => {
          setShowCustomFieldDialog(false)
          toast.success(`Updated custom field for ${label}`)
        },
      }
    )
  }

  function confirmBulkDemote() {
    setShowDemoteConfirm(false)
    bulkDemoteClients(
      { client_ids: recordIds },
      {
        onSuccess: () => {
          toast.success(`Converted ${label} to ${pluralize(recordCount, "prospect")}`)
        },
      }
    )
  }

  function confirmBulkArchive() {
    setShowArchiveConfirm(false)
    bulkArchiveToPastClient(
      { client_ids: recordIds },
      {
        onSuccess: () => {
          toast.success(`Archived ${label} to past ${pluralize(recordCount, "client")}`)
        },
      }
    )
  }

  function handleConfirmAddToProject(
    projectTemplateId: string,
    assignedToId: string | null,
    assignedTeamId: string | null
  ) {
    bulkAddClientsToProject(
      {
        client_ids: recordIds,
        project_template_id: projectTemplateId,
        assigned_to_id: assignedToId,
        assigned_team_id: assignedTeamId,
      },
      {
        onSuccess: (response) => {
          setShowAddToProjectDialog(false)
          toast.success(response.message)
        },
      }
    )
  }

  function handleConfirmAddToSequence(
    sequenceId: string,
    emailIntegrationId?: string | null,
    smsIntegration?: string | null
  ) {
    bulkAddClientsToSequence(
      {
        sequence_id: sequenceId,
        client_ids: recordIds,
        email_integration_id: emailIntegrationId ?? undefined,
        sms_integration: smsIntegration ?? undefined,
      },
      {
        onSuccess: (response) => {
          setShowAddToSequenceDialog(false)
          toast.success(response.message)
        },
      }
    )
  }

  function handleConfirmMoveToBook(destinationBookId: string) {
    bulkMoveToBook(
      { household_ids: recordIds, destination_book_id: destinationBookId },
      {
        onSuccess: () => {
          setShowMoveToBookDialog(false)
          toast.success(`Moving ${label} to another book. This may take a few moments.`)
        },
      }
    )
  }

  function handleConfirmRename(templates: {
    include_spouse: boolean
    no_spouse_template: string
    same_last_name_template: string
    different_last_name_template: string
  }) {
    bulkRenameClients(
      { client_ids: recordIds, templates },
      {
        onSuccess: () => {
          setShowRenameDialog(false)
          toast.success(`Renaming ${label}`)
        },
      }
    )
  }

  function handleSendEmail() {
    const recipientIds = recordIds.map((id) => ({
      type: "household" as const,
      id: String(id),
    }))
    bulkEmailCompose.open(recipientIds)
  }

  return (
    <>
      {creatingCustomField && (
        <CreateCustomFieldDialog entityType="Household" onClose={() => setCreatingCustomField(false)} />
      )}
      <div className="bg-primary text-background flex items-center gap-2 rounded-lg p-2">
        <Button variant="inverse" size="sm" onClick={onClearSelection} disabled={performingAction}>
          <X />
          {label} selected
        </Button>
        <Separator orientation="vertical" className="!bg-muted-foreground h-4" />
        <BulkTierSelection onSelect={handleBulkUpdateTier} isDisabled={performingAction} isUpdating={isBulkUpdating} />
        <Separator orientation="vertical" className="!bg-muted-foreground h-4" />
        <BulkTagSelection onSelect={handleBulkAddTag} isDisabled={performingAction} isUpdating={isBulkAddingTag} />
        <Separator orientation="vertical" className="!bg-muted-foreground h-4" />
        {recordCount > MAX_BULK_EMAIL_RECIPIENTS ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="inverse" size="sm" disabled>
                  <Megaphone />
                  Bulk email
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent sideOffset={10}>
              Bulk email sending limit ({MAX_BULK_EMAIL_RECIPIENTS}) exceeded
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button variant="inverse" size="sm" onClick={handleSendEmail} disabled={performingAction}>
            <Megaphone />
            Bulk email
          </Button>
        )}
        <Separator orientation="vertical" className="!bg-muted-foreground h-4" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="inverse" size="sm" disabled={performingAction}>
              <MoreHorizontal />
              More
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setCreatingCustomField(true)}>
              <Plus />
              Add custom field
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowAddToProjectDialog(true)}>
              <FolderPlus />
              Add to project
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowAddToSequenceDialog(true)}>
              <ListPlus />
              Add to sequence
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowCustomFieldDialog(true)}>
              <PenLine />
              Update custom field
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowRenameDialog(true)}>
              <Type />
              Rename households
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowDemoteConfirm(true)}>
              <ArrowLeft />
              Convert to prospect
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowArchiveConfirm(true)}>
              <Archive />
              Archive to past client
            </DropdownMenuItem>
            {canMoveToBook && (
              <DropdownMenuItem onSelect={() => setShowMoveToBookDialog(true)}>
                <ArrowRightLeft />
                Move to another book
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <BulkAddToProjectDialog
        open={showAddToProjectDialog}
        onOpenChange={setShowAddToProjectDialog}
        onConfirm={handleConfirmAddToProject}
        isSubmitting={isBulkStartingProjects}
        selectedHouseholdCount={recordCount}
      />

      <BulkAddToSequenceDialog
        open={showAddToSequenceDialog}
        onOpenChange={setShowAddToSequenceDialog}
        onConfirm={handleConfirmAddToSequence}
        isSubmitting={isBulkAddingToSequence}
        selectedHouseholdCount={recordCount}
      />

      <ConfirmDialog
        open={showDemoteConfirm}
        onOpenChange={setShowDemoteConfirm}
        title={"Convert clients to prospects"}
        description={`Are you sure you want to convert ${label} to ${pluralize(recordCount, "prospect")}? This will move them to the prospects tab and remove their financial data.`}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowDemoteConfirm(false) }}
        action={{ label: "Convert to prospects", onClick: confirmBulkDemote }}
      />

      <ConfirmDialog
        open={showArchiveConfirm}
        onOpenChange={setShowArchiveConfirm}
        title={"Archive clients to past clients"}
        description={`Are you sure you want to archive ${label} as past ${pluralize(recordCount, "client")}? This will move them to the past clients section. Their data will be preserved and they can be restored at any time.`}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowArchiveConfirm(false) }}
        action={{ label: "Archive to past clients", onClick: confirmBulkArchive }}
      />

      {canMoveToBook && (
        <MoveToBookDialog
          open={showMoveToBookDialog}
          onOpenChange={setShowMoveToBookDialog}
          onConfirm={handleConfirmMoveToBook}
          isSubmitting={isBulkMoving}
          selectedHouseholdCount={recordCount}
          entityType="client"
        />
      )}

      <BulkCustomFieldUpdate
        open={showCustomFieldDialog}
        onOpenChange={setShowCustomFieldDialog}
        onConfirm={handleBulkUpdateCustomField}
        isSubmitting={isBulkUpdating}
        selectedCount={recordCount}
        entityLabel="client"
      />

      <BulkRenameDialog
        open={showRenameDialog}
        onOpenChange={setShowRenameDialog}
        onConfirm={handleConfirmRename}
        isSubmitting={isBulkRenaming}
        selectedCount={recordCount}
        entityLabel="client"
      />
    </>
  )
}

function ProspectCohortActions({ recordIds, recordCount, onClearSelection }: CohortActionProps) {
  const label = pluralize(recordCount, "prospect")
  const [creatingCustomField, setCreatingCustomField] = useState(false)
  const bulkEmailCompose = useBulkEmailCompose()

  const { mutate: bulkUpdateProspects, isPending: isBulkUpdating } = useBulkUpdateProspects()
  const { mutate: bulkPromoteProspects, isPending: isBulkPromoting } = useBulkPromoteProspects()
  const { mutate: bulkAddProspectsToProject, isPending: isBulkStartingProjects } = useBulkAddProspectsToProject()
  const { mutate: bulkAddProspectsToSequence, isPending: isBulkAddingToSequence } = useBulkAddProspectsToSequence()
  const { mutate: bulkMoveToBook, isPending: isBulkMoving } = useBulkMoveHouseholdsToBook()
  const { mutate: bulkRenameProspects, isPending: isBulkRenaming } = useBulkRenameProspects()
  const [isBulkAddingTag, setIsBulkAddingTag] = useState(false)
  const [showPromoteConfirm, setShowPromoteConfirm] = useState(false)
  const [showAddToProjectDialog, setShowAddToProjectDialog] = useState(false)
  const [showAddToSequenceDialog, setShowAddToSequenceDialog] = useState(false)
  const [showCustomFieldDialog, setShowCustomFieldDialog] = useState(false)
  const [showMoveToBookDialog, setShowMoveToBookDialog] = useState(false)
  const [showRenameDialog, setShowRenameDialog] = useState(false)

  const { data: currentUser } = useFetchCurrentUser()
  const canMoveToBook = (currentUser?.books?.length ?? 0) > 1

  const performingAction =
    isBulkUpdating ||
    isBulkPromoting ||
    isBulkStartingProjects ||
    isBulkAddingToSequence ||
    isBulkAddingTag ||
    isBulkMoving ||
    isBulkRenaming

  function handleBulkAddTag(tagId: string) {
    setIsBulkAddingTag(true)
    bulkUpdateProspects(
      { prospect_ids: recordIds, tag_id: tagId },
      {
        onSuccess: () => {
          setIsBulkAddingTag(false)
          toast.success(`Added tag to ${label}`)
        },
        onError: () => setIsBulkAddingTag(false),
      }
    )
  }

  function handleBulkUpdateCustomField(fieldKey: string, value: string | string[] | number | boolean | null) {
    bulkUpdateProspects(
      { prospect_ids: recordIds, custom_field_key: fieldKey, custom_field_value: value },
      {
        onSuccess: () => {
          setShowCustomFieldDialog(false)
          toast.success(`Updated custom field for ${label}`)
        },
      }
    )
  }

  function confirmBulkPromote() {
    setShowPromoteConfirm(false)
    bulkPromoteProspects(
      { prospect_ids: recordIds },
      {
        onSuccess: () => {
          toast.success(`Promoted ${label} to ${pluralize(recordCount, "client")}`)
        },
      }
    )
  }

  function handleConfirmAddToProject(
    projectTemplateId: string,
    assignedToId: string | null,
    assignedTeamId: string | null
  ) {
    bulkAddProspectsToProject(
      {
        prospect_ids: recordIds,
        project_template_id: projectTemplateId,
        assigned_to_id: assignedToId,
        assigned_team_id: assignedTeamId,
      },
      {
        onSuccess: (response) => {
          setShowAddToProjectDialog(false)
          toast.success(response.message)
        },
      }
    )
  }

  function handleConfirmAddToSequence(
    sequenceId: string,
    emailIntegrationId?: string | null,
    smsIntegration?: string | null
  ) {
    bulkAddProspectsToSequence(
      {
        sequence_id: sequenceId,
        prospect_ids: recordIds,
        email_integration_id: emailIntegrationId ?? undefined,
        sms_integration: smsIntegration ?? undefined,
      },
      {
        onSuccess: (response) => {
          setShowAddToSequenceDialog(false)
          toast.success(response.message)
        },
      }
    )
  }

  function handleConfirmMoveToBook(destinationBookId: string) {
    bulkMoveToBook(
      { household_ids: recordIds, destination_book_id: destinationBookId },
      {
        onSuccess: () => {
          setShowMoveToBookDialog(false)
          toast.success(`Moving ${label} to another book. This may take a few moments.`)
        },
      }
    )
  }

  function handleConfirmRename(templates: {
    include_spouse: boolean
    no_spouse_template: string
    same_last_name_template: string
    different_last_name_template: string
  }) {
    bulkRenameProspects(
      { prospect_ids: recordIds, templates },
      {
        onSuccess: () => {
          setShowRenameDialog(false)
          toast.success(`Renaming ${label}`)
        },
      }
    )
  }

  function handleSendEmail() {
    const recipientIds = recordIds.map((id) => ({
      type: "household" as const,
      id: String(id),
    }))
    bulkEmailCompose.open(recipientIds)
  }

  return (
    <>
      {creatingCustomField && (
        <CreateCustomFieldDialog entityType="Household" onClose={() => setCreatingCustomField(false)} />
      )}
      <div className="bg-primary text-background flex items-center gap-2 rounded-lg p-2">
        <Button variant="inverse" size="sm" onClick={onClearSelection} disabled={performingAction}>
          <X />
          {label} selected
        </Button>
        <Separator orientation="vertical" className="!bg-muted-foreground h-4" />
        <BulkTagSelection onSelect={handleBulkAddTag} isDisabled={performingAction} isUpdating={isBulkAddingTag} />
        <Separator orientation="vertical" className="!bg-muted-foreground h-4" />
        {recordCount > MAX_BULK_EMAIL_RECIPIENTS ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="inverse" size="sm" disabled>
                  <Megaphone />
                  Bulk email
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent sideOffset={10}>
              Bulk email sending limit ({MAX_BULK_EMAIL_RECIPIENTS}) exceeded
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button variant="inverse" size="sm" onClick={handleSendEmail} disabled={performingAction}>
            <Megaphone />
            Bulk email
          </Button>
        )}
        <Separator orientation="vertical" className="!bg-muted-foreground h-4" />
        <Button variant="inverse" size="sm" onClick={() => setShowPromoteConfirm(true)} disabled={performingAction}>
          <ArrowRight />
          Promote to client
        </Button>
        <Separator orientation="vertical" className="!bg-muted-foreground h-4" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="inverse" size="sm" disabled={performingAction}>
              <MoreHorizontal />
              More
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setCreatingCustomField(true)}>
              <Plus />
              Add custom field
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowAddToProjectDialog(true)}>
              <FolderPlus />
              Add to project
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowAddToSequenceDialog(true)}>
              <ListPlus />
              Add to sequence
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowCustomFieldDialog(true)}>
              <PenLine />
              Update custom field
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowRenameDialog(true)}>
              <Type />
              Rename households
            </DropdownMenuItem>
            {canMoveToBook && (
              <DropdownMenuItem onSelect={() => setShowMoveToBookDialog(true)}>
                <ArrowRightLeft />
                Move to another book
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <BulkAddToProjectDialog
        open={showAddToProjectDialog}
        onOpenChange={setShowAddToProjectDialog}
        onConfirm={handleConfirmAddToProject}
        isSubmitting={isBulkStartingProjects}
        selectedHouseholdCount={recordCount}
      />

      <BulkAddToSequenceDialog
        open={showAddToSequenceDialog}
        onOpenChange={setShowAddToSequenceDialog}
        onConfirm={handleConfirmAddToSequence}
        isSubmitting={isBulkAddingToSequence}
        selectedHouseholdCount={recordCount}
      />

      <ConfirmDialog
        open={showPromoteConfirm}
        onOpenChange={setShowPromoteConfirm}
        title={"Promote prospects to clients"}
        description={`Are you sure you want to promote ${label} to ${pluralize(recordCount, "client")}?`}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowPromoteConfirm(false) }}
        action={{ label: "Promote", onClick: confirmBulkPromote }}
      />

      {canMoveToBook && (
        <MoveToBookDialog
          open={showMoveToBookDialog}
          onOpenChange={setShowMoveToBookDialog}
          onConfirm={handleConfirmMoveToBook}
          isSubmitting={isBulkMoving}
          selectedHouseholdCount={recordCount}
          entityType="prospect"
        />
      )}

      <BulkCustomFieldUpdate
        open={showCustomFieldDialog}
        onOpenChange={setShowCustomFieldDialog}
        onConfirm={handleBulkUpdateCustomField}
        isSubmitting={isBulkUpdating}
        selectedCount={recordCount}
        entityLabel="prospect"
      />

      <BulkRenameDialog
        open={showRenameDialog}
        onOpenChange={setShowRenameDialog}
        onConfirm={handleConfirmRename}
        isSubmitting={isBulkRenaming}
        selectedCount={recordCount}
        entityLabel="prospect"
      />
    </>
  )
}
